export interface FetcherInitOptions {
  baseUrl: string;
  method?: string;
  origin?: string;
  headers?: Record<string, string>;
  silent?: true;
}

export type FetcherExecOptions = Pick<FetcherInitOptions, 'headers'>;

export type Fetcher<T, U> = (body: T, options?: FetcherExecOptions) => Promise<FetchResult<U>>;

type FetchResult<T> =
  | { ok: true; result: T; error: undefined }
  | { ok: false; result: undefined; error: string };

interface MaybeMessageContainer {
  message?: string;
}

const defaultApiUrl = (() => {
  if (process.env.REACT_APP_API_URL) {
    console.debug(
      'Fetcher origin resolution: using process.env.REACT_APP_API_URL.',
      process.env.REACT_APP_API_URL
    );

    return process.env.REACT_APP_API_URL;
  }

  console.debug(
    'Fetcher origin resolution: process.env.REACT_APP_API_URL is not defined. Falling back to window.origin.',
    window.origin
  );

  return window.origin;
})();

export function createFetcher<T = null, U = unknown>(init: FetcherInitOptions): Fetcher<T, U> {
  const { baseUrl, method, origin = defaultApiUrl, silent } = init;

  return async (data: T, options?: FetcherExecOptions): Promise<FetchResult<U>> => {
    const url = `${origin}${baseUrl}`;
    const body = data ? JSON.stringify(data) : null;
    const headers = {
      'Content-Type': 'application/json',
      ...init.headers,
      ...options?.headers
    };

    let response;
    try {
      response = await fetch(url, { headers, method, body });
    } catch (e) {
      console.error(`fetch failed to execute: ${e}`, {
        url,
        body: body && `<length=${body.length}>`
      });
      return handleResponse<U>(
        new Error("Hmmm... we aren't able to communicate with the server. Please try again later.")
      );
    }

    let content;
    try {
      content = await response.text();
    } catch (e) {
      console.error(`fetch read failed: ${e}`, {
        url: response.url,
        status: response.status,
        statusText: response.statusText
      });
      return handleResponse<U>(
        new Error("The server didn't give us a response we can read. Please try again later.")
      );
    }

    if (silent) {
      return handleResponse<U>(null as U, response.status);
    }

    try {
      return handleResponse<U>(JSON.parse(content) as U, response.status);
    } catch (e) {
      console.error(`failed to decode response: ${e}, ${content}`, {
        url: response.url,
        status: response.status,
        statusText: response.statusText
      });
      return handleResponse<U>(
        new Error(
          `Oops! Something didn't go quite right. We couldn't understand the response from the server.`
        )
      );
    }
  };
}

function handleResponse<T>(object: T | Error, statusCode?: number): FetchResult<T> {
  if (object instanceof Error) {
    return { ok: false, error: object.message, result: undefined };
  }

  if (!statusCode || statusCode > 299) {
    const defaultMessage = statusCode
      ? `Sorry, that request failed with status code ${statusCode}. That's all we know.`
      : `Sorry, that request failed. You should reach out for assistance.`;
    const message = (object as MaybeMessageContainer)?.message;

    return {
      ok: false,
      error: message || defaultMessage,
      result: undefined
    };
  }

  return { ok: true, result: object as T, error: undefined };
}
